// @flow
import React from 'react';
import styled from '@emotion/styled';
import moment from 'moment';

import PlayerSelectionFilterBar from './PlayerSelectionFilterBar';
import PlayerPositionsBar from './PlayerPositionsBar';
import PlayerSelectionTable from './PlayerSelectionTable';

type Props = {
  autoAdvancePositions?: boolean,
  lastUpdatedAt?: string,
  onAdd: Object => void,
  onPositionSelect: string => void,
  onRemove: string => void,
  players: Array<*>,
  positionFilters?: Array<string>,
  maxExposurePerPlayer: number,
  positions: Array<*>,
  requiredSelections?: number,
  salaryCap?: number,
  salaryExpenditure?: number,
  sportHasTeam?: boolean,
  useAverage?: boolean,
  // searchTerm?: string,
  totalSelected: number,
  onSelectSettingsTab: (tab: string) => void,
  onToggleEntityProperty: () => void,
  onUpdateEntityProperty: (id: string, field: string, value: any) => void,
  isCaptainContest?: boolean
};

const defaultProps = {
  autoAdvancePositions: false,
  lastUpdatedAt: undefined,
  positionFilters: [],
  requiredSelections: 0,
  sportHasTeam: true,
  useAverage: false,
  salaryCap: 0,
  salaryExpenditure: 0,
  isCaptainContest: false
  // searchTerm: undefined
};

const Wrapper = styled.div(({ theme }) => ({
  [theme.mediaQuery.desktopAndUp]: {
    paddingLeft: theme.spacing.tiny,
    paddingRight: theme.spacing.small
  }
}));

const Row = styled.div({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  justifyContent: 'space-between'
});

const Title = styled.h3(({ theme }) => ({
  [theme.mediaQuery.tabletPortraitAndBelow]: {
    paddingLeft: theme.spacing.tiny
  },
  fontSize: '1.11rem',
  textTransform: 'uppercase',
  margin: '1rem 0'
}));

const PlayerSelection = ({
  autoAdvancePositions,
  onAdd,
  onRemove,
  onToggleEntityProperty,
  maxExposurePerPlayer,
  onPositionSelect,
  onUpdateEntityProperty,
  onSelectSettingsTab,
  positions,
  positionFilters,
  totalSelected,
  players,
  salaryCap,
  sportHasTeam,
  useAverage,
  salaryExpenditure,
  requiredSelections,
  lastUpdatedAt,
  isCaptainContest
}: Props) => {
  // because players have multiple positions, we only want to multiply by the captain factor if the captain filter is selected. This is a hack to make this multiplication happen here
  const isCaptainFilter = isCaptainContest && positionFilters.includes('captain');

  return (
    <Wrapper>
      <Row>
        <Title>Player {useAverage ? 'Averages' : 'Projections'}</Title>
        <span>
          Last updated at{' '}
          {moment
            .utc(lastUpdatedAt)
            .local()
            .format('h:mm a MMM Do')}
        </span>
      </Row>
      <PlayerPositionsBar
        autoAdvancePositions={autoAdvancePositions}
        onSelect={onPositionSelect}
        positions={positions}
        requiredSelections={requiredSelections}
        selectedPositions={positionFilters}
        totalSelected={totalSelected}
        isCaptainContest={isCaptainContest}
      />
      <PlayerSelectionFilterBar useAverage={useAverage} />
      <PlayerSelectionTable
        players={players}
        onAdd={onAdd}
        onRemove={onRemove}
        onToggleEntityProperty={onToggleEntityProperty}
        onUpdateEntityProperty={onUpdateEntityProperty}
        onSelectSettingsTab={onSelectSettingsTab}
        positions={positions}
        sportHasTeam={sportHasTeam}
        useAverage={useAverage}
        requiredSelections={requiredSelections}
        maxExposurePerPlayer={maxExposurePerPlayer}
        salaryCap={salaryCap}
        salaryExpenditure={salaryExpenditure}
        isCaptainFilter={isCaptainFilter}
      />
    </Wrapper>
  );
};

PlayerSelection.defaultProps = defaultProps;

export default React.memo(PlayerSelection);
